import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { buildGetSelectionAllQueryKey, useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';

import { Offer } from '../SharedMenuEditorPage/types';

export type OfferDraftChange = Partial<Offer>;
export type OfferDraftChanges = Record<string, OfferDraftChange>;
export type DraftUpdateParams = (offer: Offer, draft: OfferDraftChange | undefined) => OfferDraftChange | undefined;

export const useOfferDraftChanges = () => {
  const queryClient = useQueryClient();
  const scope = useScopeContext();
  const { offers, selection } = useSelectionData();
  const [offerDraftChanges, setOfferDraftChanges] = useState<OfferDraftChanges>({});

  const offersWithDraftChanges = useMemo(
    () =>
      offers.map(offer => {
        const draft = offerDraftChanges[offer.id];

        if (draft) {
          return {
            ...offer,
            ...draft,
          };
        }

        return offer;
      }),
    [offers, offerDraftChanges],
  );

  const updateOfferDraftChanges = (offerId: string, update: DraftUpdateParams) => {
    const offer = offers.find(offer => offer.id === offerId);

    if (!offer) {
      return;
    }

    setOfferDraftChanges(draftChanges => {
      const offerChanges = update(offer, draftChanges[offerId]);

      if (!offerChanges) {
        const { [offerId]: _, ...rest } = draftChanges;
        return rest;
      }

      return {
        ...draftChanges,
        [offerId]: offerChanges,
      };
    });
  };

  const resetDraftChanges = () => {
    const includeDraft = scope.type !== 'venue';
    queryClient
      .invalidateQueries(buildGetSelectionAllQueryKey(selection.id, includeDraft))
      .then(() => setOfferDraftChanges({}));
  };

  return {
    updateOfferDraftChanges,
    offerDraftChanges,
    offersWithDraftChanges,
    resetDraftChanges,
  };
};
